import { Button, Card, Flex, Grid, Strong, Text } from '@radix-ui/themes'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { listBudgetGoals } from '../service'
import { EmptyState } from '~/components/EmptyState'
import _ from 'lodash'
import { DateTime } from 'luxon'
import { Link, useNavigate } from '@tanstack/react-router'
import { HiCalendar } from 'react-icons/hi2'
import { formatNumber } from '~/utils/formating'
import { BudgetGoalSchema } from '../schemas'
import LinkButton from '~/components/LinkButton'
import { ArrowRightIcon } from '@radix-ui/react-icons'

type Props = {
  siteID: string
  readonly?: boolean
  metricID?: string
  stationID?: string
}

const BudgetGoalList = ({ siteID, readonly, metricID, stationID }: Props) => {
  const { t } = useTranslation('sites')
  const navigate = useNavigate()

  const { data: bugetGoals = [], isPending } = useQuery({
    queryKey: ['budget-goals', siteID],
    queryFn: () => listBudgetGoals({ siteID, metricID, stationID }),
    throwOnError: true
  })

  const navigateToNew = () => {
    navigate({
      to: '/sites/$siteID/config/budget/new',
      params: { siteID },
      search: { metricID, stationID }
    })
  }

  const formatDateRange = (startDate: Date, endDate: Date) =>
    `${DateTime.fromJSDate(startDate).toLocaleString({
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })} - ${DateTime.fromJSDate(endDate).toLocaleString({
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })}`

  const formatGoalWithMeasure = (goal: BudgetGoalSchema) => (
    <>
      {formatNumber(goal.goal)}&nbsp;
      <Strong className="text-gray-10">
        {goal.measure === 'currency' ? (
          '€'
        ) : (
          <>
            m<sup>3</sup>
          </>
        )}
      </Strong>
    </>
  )

  return (
    <Flex gap="2" direction={'column'}>
      {bugetGoals.length === 0 && !isPending ? (
        <Card className="p-5">
          <EmptyState
            title={t('budget.objectives.emptyState.title')}
            description={t('budget.objectives.emptyState.description')}
            primaryAction={
              readonly
                ? undefined
                : {
                    action: navigateToNew,
                    label: t('budget.objectives.emptyState.addObjective')
                  }
            }
          />
        </Card>
      ) : (
        <Grid
          columns={'4'}
          gap={'2'}
          className="border-[1px] border-gray-4 rounded-md grid-cols-[1.8fr_repeat(2,1fr)_160px]"
        >
          <Grid className="p-2 col-[1/-1] grid-cols-subgrid bg-gray-2 dark:bg-gray-3  text-center font-medium">
            <Flex align={'center'} gap={'1'} m="auto">
              <HiCalendar className="text-gray-10" />
              <Text size={'2'}>&nbsp;{t('budget.invoiceSection.headers.period')}</Text>
            </Flex>
            <Text size={'2'}>{t('budget.objectives.objective')}</Text>
            <Text size={'2'}>{t('budget.objectives.headers.scope')}</Text>
            <div />
          </Grid>
          {bugetGoals.map((goal) => (
            <Grid key={goal.id} className="py-2 px-4 h-10 col-[1/-1] grid-cols-subgrid text-center">
              <Text size={'2'}>{formatDateRange(goal.startDate, goal.endDate)}</Text>
              <Text size={'2'}>{formatGoalWithMeasure(goal)}</Text>
              {readonly && (
                <Text size={'2'} className="text-gray-11">
                  {goal.station?.name ?? goal.metric?.name ?? ''}
                </Text>
              )}
              {!readonly && goal.station && (
                <LinkButton
                  color="gray"
                  className="m-auto group pl-4"
                  to="/sites/$siteID/config/stations/$stationID"
                  params={{ siteID, stationID: goal.station.id }}
                  text={goal.station.name}
                  trailingIcon={
                    <ArrowRightIcon className="group-hover:opacity-100 opacity-0 transition-all duration-400 ease-in-out transform group-hover:translate-x-1" />
                  }
                />
              )}
              {!readonly && goal.metric && (
                <LinkButton
                  color="gray"
                  className="m-auto group pl-4"
                  to="/sites/$siteID/config/metrics/$metricID"
                  params={{ siteID, metricID: goal.metric.id }}
                  text={goal.metric.name}
                  trailingIcon={
                    <ArrowRightIcon className="group-hover:opacity-100 opacity-0 transition-all duration-400 ease-in-out transform group-hover:translate-x-1" />
                  }
                />
              )}
              <Flex gap={'2'} align={'center'} justify={'end'}>
                {!readonly && (
                  <Link to="/sites/$siteID/config/budget/$goalID" params={{ siteID, goalID: goal.id }}>
                    <Button size={'1'} variant="soft">
                      {t('budget.invoiceSection.form.editButton')}
                    </Button>
                  </Link>
                )}
              </Flex>
            </Grid>
          ))}
          {!readonly && (
            <Flex className="col-[1/-1] h-10" px="3" pb="3" justify={'center'}>
              <Button variant="soft" onClick={navigateToNew} className="font-medium">
                {t('budget.objectives.addObjective')}
              </Button>
            </Flex>
          )}
        </Grid>
      )}
    </Flex>
  )
}

export { BudgetGoalList }
